import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/utils-vr";
import Breadcrumbs from "@/components/vr/Breadcrumbs";
import AdSlot from "@/components/vr/AdSlot";
import Seo, { articleLd, breadcrumbLd } from "@/components/vr/Seo";
import { BookOpen, CheckCircle2, Lightbulb, Youtube, FileText, Download } from "lucide-react";

export function NotesList() {
  const [notes, setNotes] = useState([]);
  useEffect(() => { api.get("/notes?limit=50").then((r) => setNotes(r.data)); }, []);
  return (
    <div className="vr-container my-8">
      <Seo
        title="All Notes — Bihar Board Class 9-12 (Hindi)"
        description="Chapter-wise Bihar Board Notes — Class 9 से 12 तक सभी विषयों के लिए Hindi Medium में निःशुल्क नोट्स।"
        canonical="/notes"
        jsonLd={breadcrumbLd([{ name: "Home", url: "/" }, { name: "Notes", url: "/notes" }])}
      />
      <Breadcrumbs items={[{ label: "Notes" }]} />
      <h1 className="font-display font-extrabold text-3xl text-[#0F172A]">All Notes</h1>
      <p className="text-slate-600 mt-2 font-hindi">विषय एवं अध्याय के अनुसार सम्पूर्ण Hindi Medium नोट्स।</p>
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {notes.map((n) => (
          <Link key={n.id} to={`/notes/${n.slug}`} className="vr-card p-5" data-testid={`notes-list-${n.slug}`}>
            <span className="vr-chip">{n.class_slug?.replace("class-", "Class ")} · {n.subject_slug}</span>
            <h2 className="mt-2 font-display font-bold text-[#0F172A] text-base">{n.title}</h2>
            <div className="mt-1 text-sm text-slate-600 font-hindi line-clamp-2">{n.intro}</div>
          </Link>
        ))}
        {notes.length === 0 && <div className="text-slate-500 text-sm col-span-3">जल्द ही अपलोड होंगे।</div>}
      </div>
    </div>
  );
}

export function NoteReader() {
  const { slug } = useParams();
  const [note, setNote] = useState(null);
  useEffect(() => {
    api.get(`/notes/${slug}`).then((r) => setNote(r.data)).catch(() => setNote(null));
  }, [slug]);
  if (!note) return <div className="vr-container my-10 text-slate-500">Loading...</div>;
  const canonical = `/notes/${note.slug}`;
  const title = note.meta_title || `${note.title} — Bihar Board Notes`;
  const desc = note.meta_description || note.intro || `बिहार बोर्ड ${note.class_slug?.replace("class-", "Class ")} ${note.subject_slug} के ${note.title} अध्याय के हिन्दी नोट्स।`;
  const image = resolveMediaUrl(note.featured_image) || (note.youtube_id ? `https://i.ytimg.com/vi/${note.youtube_id}/maxresdefault.jpg` : undefined);
  const bc = breadcrumbLd([
    { name: "Home", url: "/" },
    { name: note.class_slug?.replace("class-", "Class ") || "Class", url: `/${note.class_slug}` },
    { name: note.subject_slug || "Subject", url: `/subjects/${note.subject_slug}` },
    { name: note.title, url: canonical },
  ]);
  const article = articleLd({
    title: note.title, description: desc, url: canonical, image,
    author: "VR Classes", published: note.published_at || note.created_at, modified: note.updated_at,
  });
  return (
    <div className="vr-container my-8 max-w-4xl">
      <Seo title={title} description={desc} canonical={canonical} image={image} type="article" jsonLd={[bc, article]} articleMeta={{ published_at: note.published_at || note.created_at, updated_at: note.updated_at, author: "VR Classes" }} />
      <Breadcrumbs items={[
        { to: `/${note.class_slug}`, label: note.class_slug?.replace("class-", "Class ") },
        { to: `/subjects/${note.subject_slug}`, label: note.subject_slug },
        { label: note.title },
      ]} />
      <article className="vr-card p-6 sm:p-8">
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0F172A]">{note.title}</h1>
        <p className="mt-2 text-slate-600 font-hindi leading-relaxed">{note.intro}</p>
        <AdSlot id="note-top" />
        <div className="prose-vr font-hindi text-slate-800 whitespace-pre-line">{note.content}</div>

        {note.definitions?.length > 0 && (
          <div className="mt-6">
            <h2 className="font-display font-bold text-lg flex items-center gap-2"><BookOpen size={18} className="text-[#1E3A8A]" /> महत्वपूर्ण परिभाषाएँ</h2>
            <div className="mt-3 space-y-2">
              {note.definitions.map((d, i) => (
                <div key={i} className="p-3 bg-blue-50 border-l-4 border-[#1E3A8A] rounded" data-testid={`def-${i}`}>
                  <h3 className="font-bold text-[#1E3A8A] font-hindi text-base">{d.term}</h3>
                  <div className="text-sm text-slate-700 font-hindi">{d.definition}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {note.key_points?.length > 0 && (
          <div className="mt-6">
            <h2 className="font-display font-bold text-lg flex items-center gap-2"><CheckCircle2 size={18} className="text-emerald-600" /> महत्वपूर्ण बिंदु</h2>
            <ul className="mt-2 space-y-1 list-disc pl-6 font-hindi text-slate-700">
              {note.key_points.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </div>
        )}

        {note.exam_tips?.length > 0 && (
          <div className="mt-6 p-4 bg-red-50 border-l-4 border-[#DC2626] rounded">
            <h2 className="font-display font-bold text-lg flex items-center gap-2"><Lightbulb size={18} className="text-[#DC2626]" /> Board Exam Tips</h2>
            <ul className="mt-2 space-y-1 list-disc pl-6 font-hindi text-slate-700">
              {note.exam_tips.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </div>
        )}

        <AdSlot id="note-bottom" />

        {note.youtube_id && (
          <div className="mt-6">
            <h2 className="font-display font-bold text-lg flex items-center gap-2"><Youtube size={18} className="text-[#DC2626]" /> Related Video</h2>
            <div className="mt-3 aspect-video">
              <iframe title={`${note.title} — YouTube Video`} className="w-full h-full rounded-lg" src={`https://www.youtube.com/embed/${note.youtube_id}`} allowFullScreen />
            </div>
          </div>
        )}

        {note.pdf_url && (
          <div className="mt-6" data-testid="note-pdf-section">
            <h2 className="font-display font-bold text-lg flex items-center gap-2"><FileText size={18} className="text-[#1E3A8A]" /> PDF Notes</h2>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <a
                href={resolveMediaUrl(note.pdf_url)}
                target="_blank"
                rel="noreferrer"
                download
                className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#1E3A8A] text-white font-semibold hover:bg-[#17307a]"
                data-testid="note-pdf-download"
              >
                <Download size={18} /> Download PDF
              </a>
              <a
                href={resolveMediaUrl(note.pdf_url)}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-semibold text-[#1E3A8A] hover:underline"
                data-testid="note-pdf-open"
              >
                Open in new tab
              </a>
            </div>
            <div className="mt-3 border border-slate-200 rounded-lg overflow-hidden bg-slate-50 hidden md:block">
              <object
                data={resolveMediaUrl(note.pdf_url)}
                type="application/pdf"
                className="w-full"
                style={{ height: "80vh" }}
                data-testid="note-pdf-viewer"
              >
                <div className="p-6 text-sm text-slate-600">
                  PDF preview is not supported on this device.{" "}
                  <a href={resolveMediaUrl(note.pdf_url)} target="_blank" rel="noreferrer" className="text-[#1E3A8A] font-semibold underline">Download the PDF</a>{" "}
                  to view it.
                </div>
              </object>
            </div>
          </div>
        )}
      </article>
    </div>
  );
}
